/**
 * Called when a `copy` event is dispatched. Modifies current selection, if any,
 * by appending a `Read more at {url}` section to the end of it.
 *
 * This happens before the selection is copied, resulting in the copied text to
 * contain the appended section.
 */
export const addLink = (window: Window): void => {
  const { document } = window;

  const selection = window.getSelection();
  if (!selection) {
    return;
  }

  // this is the extra element we append at the end of the current selection
  // before copying
  const url = document.location.href;
  const pagelink = document.createElement('p');
  pagelink.innerHTML = `Read more at: <a href="${url}">${url}</a>`;

  // this container will hold the original selection
  // + the appended pagelink p element
  // we need a container where we put the modified html, which we will then
  // select and copy again
  const newdiv = document.createElement('div');

  // the temporary container needs to be hidden off-page
  newdiv.style.position = 'absolute';
  newdiv.style.left = '-99999px';

  // cloneContents() preserves the html structure, which then preserves line
  // breaks in the full text we end up copying
  newdiv.appendChild(selection.getRangeAt(0).cloneContents());
  newdiv.appendChild(pagelink);

  // temporary container needs to be appended to the document before we can
  // select all and copy
  document.body.appendChild(newdiv);
  selection.selectAllChildren(newdiv);

  window.setTimeout(() => {
    document.body.removeChild(newdiv);
  }, 100);
};

const DISABLED_VIEWS = [
  'UserNovenaInviteIndexView',
  'UserNovenaNewView',
  'UserNovenaReviewView',
  'UserNovenaSocialView',
];

/**
 * Attaches to the copy event and makes it so the current selection gets an
 * extra section at the end of it, containing `Read more at {url}`.
 */
export const enhanceCopyPaste = (
  viewName?: string,
  callback: (window: Window) => void = addLink,
): void => {
  const isAdmin = viewName && viewName.startsWith('Admin');
  if (isAdmin) {
    return;
  }

  const isLive = viewName && viewName.startsWith('Live');
  if (isLive) {
    return;
  }

  const isDisabled = viewName && DISABLED_VIEWS.includes(viewName);
  if (isDisabled) {
    return;
  }

  document.addEventListener('copy', () => callback(window));
};
